import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  User, CheckCircle, MessageSquare, Calendar,
  Target, TrendingUp, CalendarCheck, ArrowRight,
} from 'lucide-react';

const PHASE_COLORS = {
  1: 'bg-slate-100 text-slate-600',
  2: 'bg-blue-50 text-blue-700',
  3: 'bg-violet-50 text-violet-700',
  4: 'bg-amber-50 text-amber-700',
  5: 'bg-emerald-50 text-emerald-700',
};

const SectionLabel = ({ children }) => (
  <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-3">
    {children}
  </p>
);

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' });
};

const FOLLOWUP_STATUS_LABELS = {
  ej_paborjad: 'Ej påbörjad',
  pagaende: 'Pågående',
  klar: 'Klar',
};

const ParticipantDashboard = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [home, setHome] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/me/participant-profile'),
      api.get('/dashboard/participant-home'),
    ])
      .then(([profileRes, homeRes]) => {
        setProfile(profileRes.data);
        setHome(homeRes.data);
      })
      .catch(() => toast.error('Kunde inte hämta din sida'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <p className="text-slate-400 text-sm">Laddar din sida...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!profile) {
    return (
      <DashboardLayout>
        <div className="p-8 text-slate-500 text-sm">Kunde inte läsa in data.</div>
      </DashboardLayout>
    );
  }

  const {
    participant,
    active_goals,
    attendance_summary,
    outcome_followups,
    process_phase,
  } = profile;

  const handlerInfo = home?.handler_info || null;
  const upcomingFollowups = home?.upcoming_followups || [];
  const recentMessages = home?.recent_messages || [];

  const phaseColor = PHASE_COLORS[process_phase?.current_phase] || PHASE_COLORS[1];

  return (
    <DashboardLayout>
      <div
        className="space-y-6 max-w-2xl"
        data-testid="participant-dashboard"
      >
        {/* 1 — Välkomsttext + fas-badge */}
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">
            Välkommen, {participant.first_name || participant.name}
          </h1>
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${phaseColor}`}>
              Fas {process_phase?.current_phase}: {process_phase?.label}
            </span>
            <span className="text-slate-400 text-xs">
              {participant.status === 'Avslutad' ? 'Deltagandet är avslutat' : 'Deltagande pågår'}
            </span>
          </div>
        </div>

        {/* 2 — Handläggarkort (kompakt, en rad) */}
        {handlerInfo && (
          <Card className="shadow-sm" data-testid="handler-card">
            <CardContent className="py-3 px-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <User size={14} className="text-slate-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{handlerInfo.name}</p>
                  <p className="text-xs text-slate-400 truncate">{handlerInfo.email}</p>
                </div>
                <span className="ml-auto text-xs text-slate-400 flex-shrink-0">Din handläggare</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* 3 — Mina mål */}
        {active_goals.length > 0 && (
          <Card className="shadow-sm">
            <CardContent className="pt-4">
              <SectionLabel>Mina mål</SectionLabel>
              <div className="space-y-4">
                {active_goals.map(g => (
                  <div key={g.id}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm text-slate-700 font-medium">{g.title}</span>
                      <span className="text-xs text-slate-400 ml-2 flex-shrink-0">{g.progress_percent}%</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-1.5">
                      <div
                        className="h-1.5 rounded-full transition-all"
                        style={{
                          width: `${g.progress_percent}%`,
                          backgroundColor: g.progress_percent >= 100 ? '#22c55e' : 'var(--accent-color)',
                        }}
                      />
                    </div>
                    {g.status === 'completed' && (
                      <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                        <CheckCircle size={11} />
                        Mål uppnått
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* 4 — Kommande uppföljningar */}
        <Card className="shadow-sm">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-3">
              <CalendarCheck size={14} className="text-slate-400" />
              <SectionLabel>Kommande uppföljningar</SectionLabel>
            </div>
            {upcomingFollowups.length === 0 ? (
              <p className="text-sm text-slate-400">Inga kommande uppföljningar just nu.</p>
            ) : (
              <div className="space-y-2">
                {upcomingFollowups.map(fu => (
                  <div
                    key={fu.id}
                    className="flex items-start justify-between gap-3 py-2 border-b border-slate-100 last:border-0"
                  >
                    <div className="min-w-0">
                      <p className="text-sm text-slate-700 truncate">{fu.beskrivning || fu.note_rubrik}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {fu.deadline && (
                        <span className="text-xs text-slate-400">{formatDate(fu.deadline)}</span>
                      )}
                      <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded">
                        {FOLLOWUP_STATUS_LABELS[fu.status] || fu.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* 5 — Min närvaro */}
        <Card className="shadow-sm">
          <CardContent className="pt-4">
            <SectionLabel>Närvaro — senaste 30 dagarna</SectionLabel>
            {attendance_summary.total === 0 ? (
              <p className="text-sm text-slate-400">Ingen närvaro registrerad ännu.</p>
            ) : (
              <div className="flex items-center gap-8">
                <div>
                  <p className="text-3xl font-semibold text-slate-800">{attendance_summary.rate}%</p>
                  <p className="text-xs text-slate-400 mt-0.5">Närvaro</p>
                </div>
                <div className="text-sm text-slate-500">
                  <p>{attendance_summary.present} av {attendance_summary.total} tillfällen</p>
                  {attendance_summary.rate >= 80 && (
                    <p className="text-emerald-600 text-xs mt-1 font-medium">Utmärkt närvaro</p>
                  )}
                  {attendance_summary.rate >= 60 && attendance_summary.rate < 80 && (
                    <p className="text-amber-600 text-xs mt-1 font-medium">Bra närvaro, fortsätt så</p>
                  )}
                  {attendance_summary.rate < 60 && attendance_summary.rate > 0 && (
                    <p className="text-slate-400 text-xs mt-1">Prata gärna med din handläggare</p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 6 — Meddelanden */}
        <Card className="shadow-sm">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <MessageSquare size={14} className="text-slate-400" />
                <SectionLabel>Meddelanden</SectionLabel>
              </div>
              <button
                onClick={() => navigate('/messages')}
                className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 transition-colors"
              >
                Visa alla <ArrowRight size={12} />
              </button>
            </div>
            {recentMessages.length === 0 ? (
              <p className="text-sm text-slate-400">Inga meddelanden.</p>
            ) : (
              <div className="space-y-2">
                {recentMessages.map(msg => (
                  <div
                    key={msg.id}
                    className="py-2 border-b border-slate-100 last:border-0"
                  >
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-xs font-medium text-slate-600">{msg.sender_name}</span>
                      <span className="text-xs text-slate-400">{formatDate(msg.created_at)}</span>
                    </div>
                    <p className="text-sm text-slate-500 truncate">
                      {msg.content.length > 80 ? `${msg.content.slice(0, 80)}…` : msg.content}
                    </p>
                    {!msg.is_read && (
                      <span className="inline-block mt-1 text-xs bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded">
                        Ny
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Utfallsuppföljningar (vid Avslutad — döljs annars) */}
        {outcome_followups && outcome_followups.length > 0 && (
          <Card className="shadow-sm">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp size={14} className="text-slate-400" />
                <SectionLabel>Uppföljningar efter avslut</SectionLabel>
              </div>
              <div className="space-y-2">
                {outcome_followups.map(f => (
                  <div key={f.id} className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">{f.followup_type.replace('_days', ' dagar')}</span>
                    <span className="text-slate-400 text-xs">{f.scheduled_date}</span>
                    <span className="bg-amber-50 text-amber-700 px-2 py-0.5 rounded text-xs">Kommande</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ParticipantDashboard;
